import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/schedule', label: 'Schedule' },
    { path: '/event-services', label: 'Event Services' },
    { path: '/things-to-do', label: 'Things to Do' },
    { path: '/travel-and-stay', label: 'Travel & Stay' },
    { path: '/wardrobe-planner', label: 'Wardrobe Planner' },
    { path: '/rsvp', label: 'RSVP' },
    { path: '/gifting', label: 'Gifting' },
    { path: '/our-lives', label: 'Our Lives' },
    { path: '/faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen bg-sage-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-pure-white border-b border-sage-grey-200 p-4 z-50">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-display text-sage-800 tracking-wide">
              Shivangi / Ishan
            </h1>
            <p className="text-sage-grey-600 text-xs uppercase tracking-wider">
              March 13th & 14th, 2026
            </p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-sage-700 hover:bg-sage-50 transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileMenuOpen(false)}>
          <nav className="fixed right-0 top-0 h-full w-3/4 max-w-sm bg-pure-white p-6 transform transition-transform">
            <div className="pt-16">
              <div className="space-y-6">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-3 transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'text-sage-800 border-l-2 border-sage-600 pl-4'
                        : 'text-sage-grey-600 hover:text-sage-800 hover:pl-2'
                    }`}
                  >
                    <span className="text-base font-normal tracking-wider uppercase">
                      {item.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar Navigation */}
      <nav className="hidden lg:flex fixed left-0 top-0 w-1/4 h-full bg-pure-white border-r border-sage-grey-200 flex-col overflow-hidden">
        <div className="p-8 border-b border-sage-grey-200 text-center flex-shrink-0">
          <img
            src="/images/logo.png"
            alt="Shivangi & Ishan"
            className="h-64 w-auto object-contain mx-auto"
            style={{ backgroundColor: 'transparent' }}
          />
          <h1 className="text-2xl font-display text-sage-800 tracking-wide -mt-4">
            Shivangi / Ishan
          </h1>
          <p className="text-sage-grey-600 text-sm mt-2 uppercase tracking-wider">
            March 13th and 14th, 2026.
          </p>
        </div>

        <div className="flex-1 p-12 overflow-y-auto">
          <div className="space-y-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block relative group transition-all duration-500 ${
                  location.pathname === item.path
                    ? 'text-sage-800'
                    : 'text-sage-grey-600 hover:text-sage-800'
                }`}
              >
                <span className="text-sm font-normal tracking-wider uppercase">
                  {item.label}
                </span>
                <span className={`absolute -left-12 top-1/2 -translate-y-1/2 w-px h-4 bg-sage-600 transition-all duration-500 ${
                  location.pathname === item.path
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}></span>
              </Link>
            ))}
          </div>
        </div>

      </nav>

      {/* Main Content Area */}
      <main className="flex-1 min-h-screen pt-20 lg:pt-0 lg:ml-[25%]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-8 sm:py-12 lg:py-20">
          <div className="animate-fade-in">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Layout;